import { Box } from '@material-ui/core';
import { getAgentById, PaymentMethodType, SinglePaymentMethod } from '@nemuru-eng/js-api-client';
import { useParams } from 'react-router-dom';
import { SWRConfig } from 'swr';

import { AgentPricings } from '@/components/modules/AgentDetail/ProvidersDetails/components/AgentPricings/AgentPricings';
import { Credentials } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/Credentials';
import { CredentialsType } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/types/CredentialsType';
import {
  WithLenderPaymentMethod,
  WithLenderPaymentMethodType,
} from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/types/WithLenderPaymentMethod';
import { CustomTabs } from '@/components/shared/components/CustomTabs/CustomTabs';
import { ModuleCorePermissionProps } from '@/config/types';
import { useCache } from '@/hooks/requests/useCache';
import { withPermissionsModule } from '@/utils/withPermissionsModule';

const ProvidersDetails = ({ canEdit = false }: ModuleCorePermissionProps) => {
  const { agentId } = useParams();
  if (!agentId) return null;

  const { data: agent } = useCache(getAgentById, { agentId });

  const agentHasWithLenderPaymentMethods = agent?.allowedPaymentMethodTypes.some((paymentMethod: PaymentMethodType) =>
    Object.values(WithLenderPaymentMethod).includes(paymentMethod as unknown as WithLenderPaymentMethodType),
  );
  const agentHasSinglePaymentMethods = agent?.allowedPaymentMethodTypes.some((paymentMethod: PaymentMethodType) =>
    Object.values(SinglePaymentMethod).includes(paymentMethod as unknown as SinglePaymentMethod),
  );

  const agentHasMultiplePaymentMethods = agentHasWithLenderPaymentMethods && agentHasSinglePaymentMethods;

  if (!agentHasWithLenderPaymentMethods && !agentHasSinglePaymentMethods) {
    return null;
  }

  return (
    <>
      <SWRConfig value={{ revalidateIfStale: false }}>
        <Box mt={2}>
          <CustomTabs
            variant="scrollable"
            tabs={[
              ...(agentHasWithLenderPaymentMethods
                ? [
                    {
                      name: CredentialsType.AGENT_IN_LENDER_CONTEXT,
                      panel: (
                        <>
                          <Credentials
                            canEdit={canEdit}
                            selectedCredentialsType={CredentialsType.AGENT_IN_LENDER_CONTEXT}
                          />
                          <Box mb={8} />
                          <AgentPricings canEdit={canEdit} />
                        </>
                      ),
                      iconName: 'account_balance',
                    },
                  ]
                : []),
              ...(agentHasSinglePaymentMethods
                ? [
                    {
                      name: CredentialsType.AGENT_IN_SINGLE_PAYMENT_PROVIDER_CONTEXT,
                      panel: (
                        <>
                          <Credentials
                            canEdit={canEdit}
                            selectedCredentialsType={CredentialsType.AGENT_IN_SINGLE_PAYMENT_PROVIDER_CONTEXT}
                          />
                        </>
                      ),
                      iconName: 'wallet',
                    },
                  ]
                : []),
            ]}
            tabClassProps={
              !agentHasMultiplePaymentMethods
                ? {
                    tabPanel: {
                      root: {
                        borderTop: 'none',
                      },
                    },
                  }
                : {}
            }
            style={
              !agentHasMultiplePaymentMethods
                ? {
                    display: 'none',
                  }
                : {}
            }
          />
        </Box>
      </SWRConfig>
    </>
  );
};

export const ProvidersDetailsModule = withPermissionsModule(ProvidersDetails);
